import React from 'react';
import { getProductImageURL } from '@noths/polaris-client-utils';
import Image from 'next/image';

import { MediaCountPill } from 'src/components/atoms/MediaCountPill/MediaCountPill';
import { ProductVideoSlide } from 'src/components/molecules/ProductVideoSlide/ProductVideoSlide';
import { mediaKey } from './media/buildProductMedia';
import type { MediaItem, MediaLocation } from './types';
import * as styles from './ProductMediaGridLayout.styles';

export interface ProductMediaGridLayoutProps {
  location: MediaLocation;
  media: MediaItem[];
  onSelect: (mediaIndex: number) => void;
}

const GridImage = ({
  index,
  item,
  location,
  onSelect,
  totalMedia,
}: {
  index: number;
  item: MediaItem;
  location: MediaLocation;
  onSelect: (mediaIndex: number) => void;
  totalMedia: number;
}) => {
  const label = item.type === 'video' ? item.title : `${item.title}, ${index + 1} of ${totalMedia}`;

  return (
    <button aria-label={label} css={styles.cell} onClick={() => onSelect(index)} type="button">
      {item.type === 'video' ? (
        <ProductVideoSlide
          contentPosition={index + 1}
          isActive={false}
          location={location}
          playbackId={item.playbackId}
          posterUrl={item.posterUrl}
          title={item.title}
        />
      ) : (
        <Image
          alt={label}
          css={styles.cellMedia}
          fill={true}
          priority={index === 0}
          src={getProductImageURL(item.href, index === 0 ? 'original' : 'normal')}
          unoptimized
        />
      )}
    </button>
  );
};

export const ProductMediaGridLayout = ({
  location,
  media,
  onSelect,
}: ProductMediaGridLayoutProps) => {
  const totalMedia = media.length;

  if (totalMedia === 0) {
    return null;
  }

  if (totalMedia <= 2) {
    return (
      <div css={styles.singleRoot}>
        <GridImage
          index={0}
          item={media[0]}
          key={mediaKey(media[0])}
          location={location}
          onSelect={onSelect}
          totalMedia={totalMedia}
        />
        <MediaCountPill onClick={() => onSelect(0)} totalMedia={totalMedia} />
      </div>
    );
  }

  const [mainItem, firstThumbnail, secondThumbnail] = media;
  const secondThumbnailIndex = 2;

  return (
    <div css={styles.grid}>
      <GridImage
        index={0}
        item={mainItem}
        key={mediaKey(mainItem)}
        location={location}
        onSelect={onSelect}
        totalMedia={totalMedia}
      />
      <div css={styles.thumbnailRow}>
        <GridImage
          index={1}
          item={firstThumbnail}
          key={mediaKey(firstThumbnail)}
          location={location}
          onSelect={onSelect}
          totalMedia={totalMedia}
        />
        <div css={styles.thumbnailWrapper}>
          <GridImage
            index={secondThumbnailIndex}
            item={secondThumbnail}
            key={mediaKey(secondThumbnail)}
            location={location}
            onSelect={onSelect}
            totalMedia={totalMedia}
          />
          <MediaCountPill onClick={() => onSelect(0)} totalMedia={totalMedia} />
        </div>
      </div>
    </div>
  );
};
